
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { from, interval, Subject } from 'rxjs';
import { take, takeUntil, finalize } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { SettingsService } from 'src/app/services/services/settings.service';
import { queueOverviewItem, queueOverviewConfig } from 'src/app/app-models/realTimeModels';
import { MatDialog } from '@angular/material/dialog';
import { AlarmDialogComponent } from '../real-time-dialogs/alarm-dialog/alarm-dialog.component';
import { SystemQueueDialogComponent } from '../real-time-dialogs/system-queue-dialog/system-queue-dialog.component';
import { AgentsStatusDialogComponent } from '../real-time-dialogs/agents-status-dialog/agents-status-dialog.component';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { IHelpDialogData } from 'src/app/models';

@Component({
    selector: 'app-real-time-overview',
    templateUrl: './real-time-overview.component.html',
    styleUrls: ['./real-time-overview.component.scss'],
    standalone: false
})

export class RealTimeOverviewComponent implements OnInit, OnDestroy {
  /** base columns (without action links) */
  private readonly baseColumns: string[] = [
    'queueName',
    'queueSize',
    'callback',
    'preferred',
    'ciqScheduled',
    'sla',
    'waitTimeMax',
    'agentsLoggedIn',
    'agentsInPause'
  ];

  /** columns actually rendered */
  displayedColumns: string[] = [...this.baseColumns];

  BreadcrumbsLink = [
    { "label": "Home", "url": "/" },
    { "label": "Real-Time", "url": "/real-time/overview" },
    { "label": "Queue Overview", "url": "/real-time/overview" }];

  /** whether to show action links (from backend config) */
  showActionLinks = false;

  dataSource = new MatTableDataSource<queueOverviewItem>([]);
  isLoading = true;
  private ajaxRefreshInterval = 10000;
  private terminate$ = new Subject<void>();
  showClock = false;
  filter: string = '';

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    private realTimeService: realTimeService,
    private i18nextPipe: I18NextPipe,
    private dialog: MatDialog,
    private settingsService: SettingsService
  ) {}

async ngOnInit(): Promise<void> {
  this.dataSource.sort = this.sort;

  const cfg = await this.settingsService.realtimeSettings();
  
  this.showActionLinks = cfg?.showActionLinks === true;
  this.displayedColumns = this.showActionLinks
    ? [...this.baseColumns, 'info']
    : [...this.baseColumns];

   this.showClock = false; !!cfg?.showClock;

  const intervalMs = this.normalizeInterval(cfg?.ajaxRefreshInterval);
  this.ajaxRefreshInterval = intervalMs;

  this.isLoading = false;
  this.refreshData();
  interval(intervalMs).pipe(takeUntil(this.terminate$))
    .subscribe(() => this.refreshData(false));
}

  ngOnDestroy(): void {
    this.terminate$.next();
    this.terminate$.complete();
  }

  private refreshData(showLoader = true): void {
    if (showLoader) this.isLoading = false;

    this.realTimeService.getQueueOverview()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(rows => {
        this.dataSource.data = rows ?? [];
      }, _err => {
        // keep previous data on error
      });
  }


  onAlarmClick(q: queueOverviewItem): void {
    if (!q?.queueID && q?.queueID !== 0) return;

    this.realTimeService.GetAlarmInfoForQueue(q.queueID)
      .pipe(take(1))
      .subscribe(items => {
        this.dialog.open(AlarmDialogComponent, {
          data: { items },
          width: '720px',
          maxWidth: '95vw',
          autoFocus: false
        });
      });
  }

  onDisplayQueuesInfo(q: queueOverviewItem): void {
    if (q?.queueID == null) return;

    this.realTimeService.GetSystemQueueDetails(q.queueID)
      .pipe(take(1))
      .subscribe(items => {
        this.dialog.open(SystemQueueDialogComponent, {
          data: { items },
          width: '680px',
          maxWidth: '95vw',
          autoFocus: false
        });
      });
  }

  onAgentStatusClick(q: queueOverviewItem): void {
    if (q?.queueID == null) return;

    this.dialog.open(AgentsStatusDialogComponent, {
      data: {
        queueId: q.queueID,
        ajaxRefreshInterval: this.ajaxRefreshInterval   // store this when you load config
      },
      width: '680px',
      maxWidth: '95vw',
      autoFocus: false
    });
  }

  private normalizeInterval(raw: unknown): number {
    let ms = Number(raw) || 10000;          // default 10s
    if (ms > 0 && ms <= 60) ms *= 1000;     // treat small numbers as seconds
    return Math.max(1000, ms);              // clamp to >= 1s
  }

  openHelp(): void {
    let data: IHelpDialogData = { title: this.i18nextPipe.transform('realtime:help_title_queueOverview'), text: this.i18nextPipe.transform('realtime:help_queueOverview'), renderAsHtml: true };
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: "backdrop-invisible",
      data,
      width: "600px"
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  applyFilterFromProperty() {
    this.dataSource.filter = this.filter.trim().toLowerCase();
  }

  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }


  trackByQueue = (_: number, q: queueOverviewItem) => q.queueID ?? q.queueName;
}
